import { CategoryType } from "@core/schema/CategoryType.js";
import { Locale, type LocalizedString } from "@core/schema/Locale.js";
import type { ProductForCustomer } from "@core/schema/Product.js";
import type { SubcategoryForCustomer } from "@core/schema/Subcategory.js";
import { isProductConsideredNew } from "@core/schema/utils/isProductConsideredNew.js";
import { isShippingLater } from "@core/schema/utils/isShippingLater.js";
import { tw } from "@core/utils/svelte/tw.js";
import type { ProductTagsDefinition } from "./ProductTagsDefinition.js";

const newTagClass = tw(
	"!border-[3.75px] !py-0 border-fcrdarkblue max-xs:hidden !text-fcrdarkblue font-antipol-extended",
);
const newLabel: LocalizedString = {
	[Locale.cs]: "Nové",
	[Locale.en]: "New",
};

// Unlike the decorative "new" tag this one carries availability information, so it stays visible on phones.
const preorderTagClass = tw("!border-[3.75px] !py-0 border-fcramber !text-fcramber font-antipol-extended");
const preorderLabel: LocalizedString = {
	[Locale.cs]: "Předobjednávka",
	[Locale.en]: "Pre-order",
};

/**
 * Returns the standard tags for product card:
 * - Preorder, if the product ships later and `includePreorder` is left on
 * - New, if the product can be {@link isProductConsideredNew considered new} and `includeNew` is left on.
 *   A preorder swallows it: such a product is new by definition and three tags do not fit on a card.
 * - The subcategory
 */
export function getTagsForProductCard({
	locale,
	product,
	subcategory,
	includeNew = true,
	includePreorder = true,
}: {
	product: ProductForCustomer;
	subcategory?: SubcategoryForCustomer;
	locale: Locale;
	includeNew?: boolean;
	includePreorder?: boolean;
}): ProductTagsDefinition {
	const tags: ProductTagsDefinition = {};
	if (subcategory?.category === CategoryType.Coffee) {
		tags[subcategory.name[locale]] = { background: subcategory.icon?.color };
	}
	const preorder = includePreorder && isShippingLater(product.shippingStartsAt, Date.now());
	if (preorder) {
		tags[preorderLabel[locale]] = { class: preorderTagClass };
	}
	if (!preorder && includeNew && isProductConsideredNew(product)) {
		tags[newLabel[locale]] = { class: newTagClass };
	}
	return tags;
}
